import { Fragment, useContext, useEffect, useState, useRef } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate, Link } from 'react-router-dom';
import { RegisteredInputsBuilder, ApiRequestBuilder, ButtonBuilder, VerticalButtonGroup } from '../../../../../../GlobalComponents/Builders';
import { Row, Col, CardBody, Form, Card } from 'reactstrap'
import { buildOptionsFromArray, submitHandler, groupObjectsByKey, reduceArray } from '../../../../../../GlobalComponents/Helpers';
import { AccountContext } from '../../../../../../GlobalComponents/Authentication/Accounts';
import DMTProgressDashboard from '../../../Jobs/Job/DMT/components/progress/DMTProgress';

const DMTSearch = () => {

    document.title = 'Search Records'

    const form = useForm();
    const { handleSubmit, setValue } = form;
    const navigate = useNavigate();
    const { isAdmin } = useContext(AccountContext);

    const [selectedPartner, setSelectedPartner] = useState(null)
    const [jobSelected, setSelectedJob] = useState(null)
    const [allJobs, setAllJobs] = useState([])
    const partnerJobs = useRef(null)
    const [jobData, setJobData] = useState(null)

    const services = {
        'connectwise-cloud': 'ConnectWise',
        'connectwise-onprem': 'ConnectWise Manage - SQL',
        'servicenow': 'ServiceNow',
        'salesforce': 'SalesForce',
        'simplesat': 'SimpleSat',
        'csv': 'CSV',
        'autotask': 'Autotask',
        'dynamics': 'Dynamics365',
        'mavenlink': 'Mavenlink',
        'netsuite': 'NetSuite',
        'jira': 'Jira',
        'pandadoc': 'PandaDoc',
        'halo': 'Halo',
        'servicenow-ntt': 'SN - NTT',
        'shippo': 'Shippo',
        'freshservice': 'FreshService'
    }

    const getServiceName = (serviceCode) => services[serviceCode] || serviceCode || '';

    const jobTypeLabels = {
        'migration': 'Migration',
        'setup-tables': 'Setup Tables',
        'file-import': 'File Import',
        'one-time-update': 'One-Time Update',
        'clean-up': 'Clean Up',
    }

    const getJobTypeLabel = (job) => {
        if (job.is_sync) return 'Sync';
        return jobTypeLabels[job.job_type] || 'Migration';
    }

    const handleJobSelection = () => {};

    const renderJobDetails = () => {
        if (!jobData) return null;
        const arrow = <span className='txt-secondary' style={{ margin: '0 6px', fontSize: 11 }}>→</span>;
        const connectionLink = (connection) => isAdmin
            ? <Link to={`${process.env.PUBLIC_URL}/connections/${connection.guid}`} style={{ textDecoration: 'none' }}>{connection.name}</Link>
            : connection.name;

        return (
            <div className='d-flex align-items-center justify-content-between flex-wrap gap-3' style={{ fontSize: 13 }}>
                <div className='d-flex align-items-center gap-4 flex-wrap'>
                    <div style={{ minWidth: 250, maxWidth: 350  }}>
                        <span style={{ fontWeight: 600 }}>{jobData.name}</span>
                        <div style={{ fontSize: 11, color: '#999', marginTop: 2 }}>
                            {getJobTypeLabel(jobData)} — #{jobData.id}{jobData.sync_job_id ? <>{arrow}{jobData.sync_job_id}</> : ''}
                        </div>
                    </div>
                    <div style={{ borderLeft: '1px solid #e0e0e0', paddingLeft: 30 }}>
                        <div style={{ display: 'grid', gridTemplateColumns: 'auto auto auto', gap: '0 0', alignItems: 'baseline' }}>
                            <div style={{ textAlign: 'right' }}>{connectionLink(jobData.source_connection)}</div>
                            <div style={{ textAlign: 'center', padding: '0 8px' }}>{arrow}</div>
                            <div style={{ color: jobData.is_production ? 'red' : '' }}>{connectionLink(jobData.destination_connection)}</div>
                            <div style={{ textAlign: 'right', fontSize: 11 }} className='txt-primary'>{getServiceName(jobData.source_connection.service_code)}</div>
                            <div></div>
                            <div style={{ fontSize: 11 }} className='txt-primary'>{getServiceName(jobData.destination_connection.service_code)}</div>
                        </div>
                    </div>
                    {!!jobData.is_production && (
                        <div style={{ borderLeft: '1px solid #e0e0e0', paddingLeft: 16 }}>
                            <span style={{ fontSize: 11, fontWeight: 600, color: 'red', textTransform: 'uppercase' }}>Production</span>
                        </div>
                    )}
                </div>
                <div className='d-flex gap-2'>
                    <VerticalButtonGroup className='btn-primary' label='Navigate' style={{ width: 160 }} buttons={[
                        <ButtonBuilder label='Job Page' className='btn btn-outline-primary' onClick={() => navigate(`${process.env.PUBLIC_URL}/tools/data-migration/jobs/${jobSelected}`)} />,
                        <ButtonBuilder label='Progress Dashboard' className='btn btn-outline-primary' onClick={() => navigate(`${process.env.PUBLIC_URL}/tools/data-migration/jobs/${jobSelected}/progress`)} />,
                        (jobData && jobData.sync_job_id ? <ButtonBuilder label={`Sync Job`} className='btn btn-outline-secondary' onClick={() => navigate(`${process.env.PUBLIC_URL}/tools/data-migration/jobs/${jobData.sync_job_id}`)} /> : null),
                        (jobData && jobData.sync_job_id ? <ButtonBuilder label={`Progress Dashboard`} className='btn btn-outline-secondary' onClick={() => navigate(`${process.env.PUBLIC_URL}/tools/data-migration/jobs/${jobData.sync_job_id}/progress`)} /> : null),
                    ]} />
                </div>
            </div>
        );
    }

    useEffect(() => {

        if(allJobs.length > 0) return

        ApiRequestBuilder('queryGet', null, null, null, { conditions: 'tool_id = 7', table: 'v_job' }).then(function (results) {
            if (results.data) {
                for (let i = 0; i < results.data.length; i++) {
                    results.data[i].label = `${results.data[i].job_id} - ${results.data[i].name}`
                }
                setAllJobs(results.data)
                partnerJobs.current = groupObjectsByKey(results.data, 'partner_code')
            }
        })

    }, []);

    return (
        <Fragment>
            <Row>
                <Col xs={12}>
                    <Form id='search-job' className='card' onSubmit={submitHandler(handleSubmit, handleJobSelection)}>
                        <CardBody style={{ padding: '20px 24px' }}>
                            <h5 className='mb-3'>Job Selection</h5>
                            <Row>
                                <RegisteredInputsBuilder
                                    form={form}
                                    inputs={[
                                        {
                                            label: 'Partner',
                                            name: 'partnerName',
                                            type: 'reactselect',
                                            options: buildOptionsFromArray(reduceArray(allJobs, 'partner_code', ['job_id','partner_code', 'partner_name']), { label: 'partner_name', value: 'partner_code' }),
                                            size: 4,
                                            settings: { topAligned: true },
                                            onChange: (e) => {
                                                setSelectedJob(null)
                                                setSelectedPartner(e.target.value)
                                                setValue('partnerName', e.target.value)
                                                setValue('jobId', null)
                                                setJobData(null)
                                            }
                                        },
                                        {
                                            label: 'Job',
                                            name: 'jobId',
                                            type: 'reactselect',
                                            options: buildOptionsFromArray(partnerJobs.current && partnerJobs.current[selectedPartner] ? partnerJobs.current[selectedPartner] : [], { label: 'label', value: 'job_id' }),
                                            size: 4,
                                            settings: { topAligned: true },
                                            visible: selectedPartner ? true : false,
                                            onChange: (e) => {
                                                if (jobSelected !== e.target.value){
                                                    setJobData(null)
                                                }
                                                setSelectedJob(e.target.value)
                                                setValue('jobId', e.target.value)
                                                setJobData(allJobs.filter(job => job.job_id === parseInt(e.target.value))[0])
                                            }
                                        }
                                    ]} />
                            </Row>
                        </CardBody>
                    </Form>
                </Col>
            </Row>

            {jobData && (
                <Row>
                    <Col xs={12}>
                        <Card>
                            <CardBody style={{ padding: '16px 24px' }}>
                                {renderJobDetails()}
                            </CardBody>
                        </Card>
                    </Col>
                </Row>
            )}

            {jobSelected && jobData && (
                <DMTProgressDashboard jobData={jobData} showDetails={false} showTiles={false} showLoader={false} />
            )}
        </Fragment>
    );
};

export default DMTSearch;
